import React, { useState, useRef, useEffect } from "react";
import {
  Item,
  Items,
  ItemsContainer,
  SelectDropdownWrapper,
  SearchContainer,
  CustomSearchInput,
  SearchIcon,
  SearchInput,
  CategoryHeader,
  ArrowIcon,
  GroupedItems,
  UngroupedItems,
  DropdownContainer,
  DropdownLabel,
} from "./SelectDropdown.styled";
import ArrowDownIcon from "../../../elements/icon/assets/arrow_down_icon.svg";
import { Icon } from "../../../elements";

const SelectDropdown = ({
  label,
  items = [],
  onSelect,
  width,
  enableSearch = false,
  itemsContainerWidth,
  itemsContainerHeight,
  defaultValue = null,
  dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const dropdownRef = useRef(null);
  const itemsContainerRef = useRef(null);

  const isGrouped = typeof items === "object" && !Array.isArray(items);

  const findItemByValue = (value) => {
    if (!value) return null;

    if (isGrouped) {
      for (const groupItems of Object.values(items)) {
        const found = groupItems.find((item) => item.value === value);
        if (found) return found;
      }
    } else {
      return items.find((item) => item.value === value) || null;
    }
    return null;
  };

  useEffect(() => {
    const foundItem = findItemByValue(defaultValue);
    setSelectedItem(foundItem);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, items]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        itemsContainerRef.current &&
        !itemsContainerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleZoomChange = () => setIsOpen(false);
    const handleScroll = () => setIsOpen(false);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleZoomChange);

    let parent = dropdownRef.current?.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (style.overflow === "auto" || style.overflow === "scroll") {
        parent.addEventListener("scroll", handleScroll);
        break;
      }
      parent = parent.parentElement;
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleZoomChange);
      if (parent) {
        parent.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setIsOpen(false);
    onSelect(item);
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownWidth(rect.width);
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e) => {
    e.stopPropagation();
    setSearchValue(e.target.value);
  };

  const toggleCategory = (key) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const filterItems = (itemsList) =>
    itemsList.filter((item) =>
      item.label.toLowerCase().includes(searchValue.toLowerCase())
    );

  const renderGroupedItems = () => (
    <GroupedItems>
      {Object.entries(items).map(([category, groupItems], catIndex) => {
        const filtered = filterItems(groupItems);
        const isCollapsed = collapsedCategories[category];
        return (
          <div key={catIndex}>
            <CategoryHeader onClick={() => toggleCategory(category)}>
              <span>
                {category} ({filtered.length})
              </span>
              <ArrowIcon
                src={ArrowDownIcon}
                alt="arrow down"
                isCollapsed={isCollapsed}
              />
            </CategoryHeader>
            {!isCollapsed &&
              filtered.map((item, idx) => (
                <Item
                  key={`${catIndex}-${idx}`}
                  onClick={() => handleItemClick(item)}
                  isSelected={selectedItem?.value === item.value}
                  role="option"
                >
                  {item.label}
                </Item>
              ))}
          </div>
        );
      })}
    </GroupedItems>
  );

  const renderUngroupedItems = () => (
    <UngroupedItems>
      {filterItems(items).map((item, index) => (
        <Item
          key={index}
          onClick={() => handleItemClick(item)}
          isSelected={selectedItem?.value === item.value}
          role="option"
        >
          {item.label}
        </Item>
      ))}
    </UngroupedItems>
  );

  return (
    <>
      <DropdownContainer ref={dropdownRef}>
        <SelectDropdownWrapper onClick={handleDropdownClick} width={width} data-testid={dataTestId}>
          <DropdownLabel>{selectedItem?.label || label}</DropdownLabel>
          <ArrowIcon src={ArrowDownIcon} alt="arrow down" marginLeft="auto" />
        </SelectDropdownWrapper>
      </DropdownContainer>

      {isOpen && (
        <ItemsContainer
          ref={itemsContainerRef}
          top={dropdownPosition.top}
          right={dropdownPosition.right}
          width={itemsContainerWidth || `${dropdownWidth}px`}
          height={itemsContainerHeight}
        >
          {enableSearch && (
            <SearchContainer>
              <CustomSearchInput>
                <SearchInput
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                  onClick={(e) => e.stopPropagation()}
                />
                <SearchIcon>
                  <Icon name="magnifier" height="20px" width="20px" />
                </SearchIcon>
              </CustomSearchInput>
            </SearchContainer>
          )}

          <Items role="listbox">
            {isGrouped ? renderGroupedItems() : renderUngroupedItems()}
          </Items>
        </ItemsContainer>
      )}
    </>
  );
};

export default SelectDropdown;
